import React, { useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { CaseHeaderInfo } from './CaseHeaderInfo';
import { CustomLoadingSpinner } from '../../../components/ui/spinners/LoadingSpinner';
import { 
  selectCaseHeaderLoadingStatus,
  selectCaseHeaderError,
  selectCaseHeaderUpdateError,
  selectIsEditMode,
  clearUpdateError,
} from '../logic/Slice';
import { fetchCaseInfo } from '../../../thunks/Case';

import ErrorDisplayV2 from '../../../components/ui/error/v2';

import { getDealingOfficers, getForwardingAuthorities, getIOAppointingAuthorities, } from '../../users/logic/slice';
import { fetchDealingOfficers, fetchForwardingAuthorities, fetchIOAppointingAuthorities } from '../../users/logic/thunk';

interface CaseHeaderInfoContainerProps {
  caseId: string;
  className?: string;
  onClickAIInsights: () => void;
}

export type InvDropdownType = 'NONE' | 'INVESTIGATOR';

const CaseHeaderInfoContainer: React.FC<CaseHeaderInfoContainerProps> = ({
  caseId,
  className,
  onClickAIInsights
}) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectCaseHeaderLoadingStatus) === 'pending';
  const fetchError = useAppSelector(selectCaseHeaderError);
  const updateError = useAppSelector(selectCaseHeaderUpdateError);
  const isEditMode = useAppSelector(selectIsEditMode);

  const forwardingAuthorities = useAppSelector(getForwardingAuthorities);
  const dealingOfficers = useAppSelector(getDealingOfficers);
  const ioAppointingAuthorities = useAppSelector(getIOAppointingAuthorities);
  
  useEffect(() => {
    if (!forwardingAuthorities.length) {
      dispatch(fetchForwardingAuthorities());
    }

    if (!dealingOfficers.length) {
      dispatch(fetchDealingOfficers());
    }

    if (!ioAppointingAuthorities.length) {
      dispatch(fetchIOAppointingAuthorities());
    }
  }, [dispatch,forwardingAuthorities.length,dealingOfficers.length,ioAppointingAuthorities.length]);

  // Retry handler
  const handleRetry = useCallback(() => {
    dispatch(clearUpdateError());
    dispatch(fetchCaseInfo({ case_id: caseId }));
  }, [dispatch, caseId]);

  if (fetchError) {
    return (
      <ErrorDisplayV2
        errorMessage={fetchError}
        onRetry={() => dispatch(fetchCaseInfo({ case_id: caseId }))}
        className='mb-6'
      />
    );
  }

  if (updateError) {
    return (
      <ErrorDisplayV2
        title="Sorry, something went wrong and we couldn't save your changes. Please refresh and try again."
        errorMessage={updateError}
        onRetry={handleRetry}
        className='mb-6'
      />
    );
  }
  
  if (isLoading && !isEditMode) {
    return (
      <CustomLoadingSpinner
        title="Loading Case Info..."
        subtitle="Fetching investigation overview, stakeholders and additional details."
        minHeight="min-h-[400px]"
      />
    );
  }

  return (
    <CaseHeaderInfo
      caseId={caseId}
      className={className}
      onClickAIInsights={onClickAIInsights}
    />
  );
};

export { CaseHeaderInfoContainer };